import React, { useMemo } from 'react';
import { Accordion, BodyText, Title } from '@kvdbil/components';
import styled from 'styled-components';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { FaqSectionBlockFragment } from '~/config/generated/graphql';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import Link from '~/App/shared/components/Link';

const BLOCK_SPACING = '2.5rem';

const Section = styled.section`
  margin: ${BLOCK_SPACING} 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterLink = styled(Link)`
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  text-decoration: none;
`;

type Props = Pick<
  FaqSectionBlockFragment,
  'title' | 'ingress' | 'questions' | 'footerText' | 'footerLink'
>;

const FaqSection = ({
  title,
  ingress,
  questions,
  footerText,
  footerLink
}: Props) => {
  const items = useMemo(
    () =>
      questions
        .filter(({ question, answer }) => question && !isEmptyDocument(answer))
        .map(({ question, answer }) => ({
          title: question ?? '',
          text: <StructuredText data={answer} />
        })),
    [questions]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Section>
      {title && <Title as="h2">{title}</Title>}
      {ingress && <BodyText>{ingress}</BodyText>}

      <Accordion items={items} />

      {(footerText || footerLink?.url) && (
        <Footer>
          {footerText && <BodyText>{footerText}</BodyText>}
          {footerLink?.url && (
            <FooterLink to={footerLink.url}>{footerLink.text}</FooterLink>
          )}
        </Footer>
      )}
    </Section>
  );
};

export default FaqSection;
